import { Component, Host, Element, Event, Prop, State, h } from '@stencil/core';
import { generateRandomNumber, hideFocusRingWhenUsingMouse, resizeSVGsForEdge } from '../../../global/utils/utils';
import iconClose from '../../../assets/icon-close.svg';
export class AccordionComponent {
  constructor() {
    /**
     * internalised Unique ID for the binding Accordion button to its panel
     */
    this.uniqueID = generateRandomNumber();
    /**
     * Toggle accordion initial state to expanded
     */
    this.isActiveAccordion = false;
    /**
     * Display a bottom border on the accordion panel when open. The last Accordion item should have this set to true
     */
    this.isLastAccordion = false;
    /**
     * The font family of the accordion label
     */
    this.labelFont = 'fontfamily02';
    /**
     * Track state changes (I.e. whether the Accordion component is open or closed)
     */
    this.toggle = false;
  }
  /**
   * Set default accordion expanded based on isActiveAccordion property value
   */
  componentDidLoad() {
    if (this.isActiveAccordion) {
      this.toggle = !this.toggle;
      this.adjustHeight('acc-' + this.uniqueID);
    }
    hideFocusRingWhenUsingMouse(this.inputElement);
    resizeSVGsForEdge(this.svgSpan, '16', '16', '16', '16', '16', '16');
    if (String(this.labelFont) !== 'null' && !['fontfamily01', 'fontfamily02'].includes(this.labelFont)) {
      throw new Error(this.labelFont + ' is not a valid font type');
    }
  }
  /**
   * This will manage the accordion component event and state changes
   */
  toggleComponent(id) {
    this.toggle = !this.toggle;
    // When the user click emit the toggle state value
    // A event can emit any type of value
    this.toggleEvent.emit({ visible: this.toggle });
    this.adjustHeight(id);
  }
  /**
   * Adjust the height of the accordion panel based on the scroll height of the slotted content, used in expand/collapse animation
   */
  adjustHeight(id) {
    const panel = this.hostElement.shadowRoot.querySelector('#' + id);
    const panelChild = this.hostElement.querySelector('[slot="panel-content"]');
    if (this.toggle) {
      panel.setAttribute('style', 'max-height: ' +
        (panelChild && panelChild.scrollHeight) +
        'px; overflow: visible; transition: max-height 0.5s ease-out, visibility 0.5s step-end;');
    }
    else {
      panel.setAttribute('style', 'max-height: 0;');
    }
  }
  /**
   * Create HTML representation of component DOM and return this
   for output to the browser DOM
   */
  render() {
    return (h(Host, null,
      h("div", { class: "accordion" },
        h("button", { ref: (el) => (this.inputElement = el), id: 'btn_acc-' + this.uniqueID, "aria-controls": 'acc-' + this.uniqueID, class: {
            'accordion-button': true,
            active: this.toggle,
            'is-last-accordion': this.isLastAccordion && !this.toggle
          }, "aria-expanded": this.toggle ? 'true' : 'false', onClick: () => this.toggleComponent('acc-' + this.uniqueID) },
          h("span", { class: {
              'accordion-label': true,
              ['agl-ds-' + this.labelFont]: true
            } }, this.accordionLabel),
          h("span", { class: { 'accordion-icon': true, expanded: this.toggle, collapsed: !this.toggle } },
            h("span", { ref: (el) => (this.svgSpan = el), "aria-hidden": "true", innerHTML: iconClose }))),
        h("div", { id: 'acc-' + this.uniqueID, "aria-labelledby": 'btn_acc-' + this.uniqueID, class: { 'accordion-panel': true, active: this.toggle, 'is-last-accordion': this.isLastAccordion }, role: "region" },
          h("slot", { name: "panel-content" })))));
  }
  static get is() { return "agl-ds-accordion"; }
  static get encapsulation() { return "shadow"; }
  static get originalStyleUrls() { return {
    "$": ["accordion.component.scss"]
  }; }
  static get styleUrls() { return {
    "$": ["accordion.component.css"]
  }; }
  static get properties() { return {
    "accordionLabel": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Text to display on the Accordion button"
      },
      "attribute": "accordion-label",
      "reflect": false
    },
    "isActiveAccordion": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Toggle accordion initial state to expanded"
      },
      "attribute": "is-active-accordion",
      "reflect": false,
      "defaultValue": "false"
    },
    "isLastAccordion": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Display a bottom border on the accordion panel when open. The last Accordion item should have this set to true"
      },
      "attribute": "is-last-accordion",
      "reflect": false,
      "defaultValue": "false"
    },
    "labelFont": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "FontType",
        "resolved": "\"fontfamily01\" | \"fontfamily02\"",
        "references": {
          "FontType": {
            "location": "import",
            "path": "../../../global/component.types"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The font family of the accordion label"
      },
      "attribute": "label-font",
      "reflect": false,
      "defaultValue": "'fontfamily02'"
    }
  }; }
  static get states() { return {
    "toggle": {}
  }; }
  static get events() { return [{
      "method": "toggleEvent",
      "name": "toggleEvent",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": "Track component events (I.e. activation of Accordion component)"
      },
      "complexType": {
        "original": "any",
        "resolved": "any",
        "references": {}
      }
    }]; }
  static get elementRef() { return "hostElement"; }
}
